import { useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
import { FLUXO_MODELO_EXTRA_LABELS, preencherMensagem, VARIAVEIS_MENSAGEM } from '../../../models/mensagensContato.modelos';
import type { FluxoModeloExtra } from '../../../models/mensagensContato.modelos';
import type { DocumentoParaMensagem } from '../../../services/mensagensContatoService';

export interface ValoresModeloEditor {
  texto: string;
  documentoIds: string[];
  /** Só modelo criado pelo SI. */
  titulo?: string;
  fluxo?: FluxoModeloExtra;
}

interface MensagemModeloEditorProps {
  inicial: ValoresModeloEditor;
  /** Modelo criado pelo SI: mostra título e "Aparece em". */
  extra: boolean;
  documentos: DocumentoParaMensagem[];
  salvando: boolean;
  onSalvar: (valores: ValoresModeloEditor) => void;
  /** "Restaurar o texto padrão" (7 modelos) ou "Excluir modelo" (criado pelo SI). */
  acaoSecundaria?: { label: string; onClick: () => void; disabled?: boolean };
}

/** Prévia como o WhatsApp mostra: *texto* em negrito. */
function comNegrito(texto: string) {
  return texto.split(/(\*[^*\n]+\*)/g).map((parte, i) =>
    parte.length > 2 && parte.startsWith('*') && parte.endsWith('*') ? (
      <strong key={i}>{parte.slice(1, -1)}</strong>
    ) : (
      parte
    )
  );
}

/** Editor de um modelo de mensagem: texto com variáveis, prévia em balão e documentos que vão
 * junto. O pai remonta com `key` ao trocar de modelo (estado inicial = `inicial`). */
export default function MensagemModeloEditor({
  inicial,
  extra,
  documentos,
  salvando,
  onSalvar,
  acaoSecundaria,
}: MensagemModeloEditorProps) {
  const { user } = useAuth();
  const [valores, setValores] = useState<ValoresModeloEditor>(inicial);
  const textoRef = useRef<HTMLTextAreaElement>(null);
  const { texto, documentoIds } = valores;
  const atualizar = (parcial: Partial<ValoresModeloEditor>) => setValores((atual) => ({ ...atual, ...parcial }));

  const alterado = JSON.stringify(valores) !== JSON.stringify(inicial);
  const valido = texto.trim() !== '' && (!extra || Boolean(valores.titulo?.trim()));
  const remetente = (user?.displayName ?? 'Time de SI').split(' ')[0];
  const previa = useMemo(
    () =>
      preencherMensagem(
        texto,
        {
          nome: 'Ana Ribeiro',
          remetente,
          codigoRastreio: 'QR123456789BR',
          dataPrevista: '04/11',
          endereco: 'Rua das Flores, 120, Apto 3 - Centro, Belo Horizonte/MG - CEP 30110-000',
        },
        documentos
          .filter((d) => documentoIds.includes(d.id))
          .map((d) => ({ titulo: d.titulo, url: 'https://…/arquivo.pdf' }))
      ),
    [texto, remetente, documentos, documentoIds]
  );

  const inserir = (chave: string) => {
    const campo = textoRef.current;
    const inicio = campo?.selectionStart ?? texto.length;
    const fim = campo?.selectionEnd ?? texto.length;
    atualizar({ texto: texto.slice(0, inicio) + chave + texto.slice(fim) });
    requestAnimationFrame(() => {
      campo?.focus();
      campo?.setSelectionRange(inicio + chave.length, inicio + chave.length);
    });
  };

  return (
    <>
      {extra && (
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <label className="block text-sm font-medium text-gray-700">
            Título do modelo
            <input
              className="input-primary mt-1 w-full"
              value={valores.titulo ?? ''}
              placeholder="Ex.: Lembrete de devolução"
              onChange={(event) => atualizar({ titulo: event.target.value })}
            />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Aparece em
            <select
              className="input-primary mt-1 w-full"
              value={valores.fluxo ?? 'todos'}
              onChange={(event) => atualizar({ fluxo: event.target.value as FluxoModeloExtra })}
            >
              {Object.entries(FLUXO_MODELO_EXTRA_LABELS).map(([valor, label]) => (
                <option key={valor} value={valor}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          <label htmlFor="mensagem-texto" className="block text-sm font-medium text-gray-700">
            Texto da mensagem
          </label>
          <div className="flex flex-wrap items-center gap-1 text-xs">
            <span className="text-gray-500">Inserir:</span>
            {VARIAVEIS_MENSAGEM.map((v) => (
              <button
                key={v.chave}
                type="button"
                onClick={() => inserir(v.chave)}
                className="rounded-full bg-primary-50 px-2 py-0.5 font-semibold text-primary-700 hover:bg-primary-100"
              >
                {v.label}
              </button>
            ))}
          </div>
          <textarea
            id="mensagem-texto"
            ref={textoRef}
            rows={12}
            className="input-primary w-full font-mono text-xs"
            value={texto}
            onChange={(event) => atualizar({ texto: event.target.value })}
          />
          <p className="text-[11px] text-gray-500">
            Negrito: coloque entre asteriscos, *assim*. No Teams o texto vai sem os asteriscos.
          </p>
          {acaoSecundaria && (
            <button
              type="button"
              className="text-xs font-semibold text-gray-500 hover:text-primary-700 disabled:opacity-50"
              disabled={acaoSecundaria.disabled}
              onClick={acaoSecundaria.onClick}
            >
              {acaoSecundaria.label}
            </button>
          )}
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Como chega para a pessoa</p>
          <div className="rounded-2xl bg-[#e5ddd5] p-4">
            <div className="ml-auto max-w-[92%] whitespace-pre-wrap rounded-xl rounded-tr-none bg-[#dcf8c6] px-3 py-2 text-[13px] leading-relaxed text-gray-900 shadow">
              {comNegrito(previa)}
            </div>
          </div>
          <p className="text-[11px] text-gray-500">
            Exemplo com Ana Ribeiro · código QR123456789BR · 04/11 · endereço fictício · enviado por você ({remetente}).
          </p>
        </div>
      </div>

      <fieldset className="space-y-2 rounded-xl border border-gray-200 p-4">
        <legend className="px-1 text-sm font-medium text-gray-700">📎 Documentos que acompanham esta mensagem</legend>
        <p className="text-xs text-gray-500">
          Documentos publicados no Portal do SI. Entram onde estiver <code>{'{documentos}'}</code> (ou no fim do texto) como link,
          e podem ir como anexo pelo botão &quot;Compartilhar com anexo&quot;. Versão nova do documento é usada sozinha.
        </p>
        {documentos.length === 0 ? (
          <p className="text-xs text-gray-400">Nenhum documento publicado no Portal do SI ainda.</p>
        ) : (
          <div className="flex flex-wrap gap-2 text-xs">
            {documentos.map((d) => {
              const marcado = documentoIds.includes(d.id);
              return (
                <label
                  key={d.id}
                  className={clsx(
                    'flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5',
                    marcado ? 'border-primary-300 bg-primary-50 font-semibold text-primary-800' : 'border-gray-200 text-gray-700'
                  )}
                >
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() =>
                      atualizar({ documentoIds: marcado ? documentoIds.filter((id) => id !== d.id) : [...documentoIds, d.id] })
                    }
                  />
                  {d.titulo} <span className="font-normal text-gray-500">v{d.versao}</span>
                </label>
              );
            })}
          </div>
        )}
      </fieldset>

      <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
        <button type="button" className="btn-secondary" disabled={!alterado} onClick={() => setValores(inicial)}>
          Descartar
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={!alterado || !valido || salvando}
          onClick={() => onSalvar(valores)}
        >
          {salvando ? 'Salvando...' : 'Salvar modelo'}
        </button>
      </div>
    </>
  );
}
